import React, { useState } from 'react';
import { Share2, MessageCircle, Smartphone, Copy, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const ShareTripModal = ({ booking, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!booking) return null;

  const tripDate = booking.trip?.date || 'N/A';
  const departureTime = booking.trip?.departureTime || 'N/A';
  const origin = booking.boarding?.city || booking.route?.origin?.city || 'N/A';
  const destination = booking.alighting?.city || booking.route?.destination?.city || 'N/A';
  const ticketCode = booking.ticketCode || 'N/A';
  const operatorName = booking.operatorName || 'N/A';
  const passengers = booking.passengers?.map(p => p.name).join(', ') || 'N/A';
  const seats = booking.passengers?.map(p => p.seat).join(', ') || 'N/A';

  const shareMessage = `🚌 My Tekstar Bus Trip

📍 ${origin} → ${destination}
📅 ${tripDate} at ${departureTime}
🎫 Ticket: ${ticketCode}
🚌 Operator: ${operatorName}
👥 Passengers: ${passengers}
💺 Seats: ${seats}

Book your bus ticket at tekstar.com`;

  const handleWhatsAppShare = () => {
    const encodedMessage = encodeURIComponent(shareMessage);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleSMSShare = () => {
    const encodedMessage = encodeURIComponent(shareMessage);
    window.open(`sms:?body=${encodedMessage}`, '_blank');
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'Trip details copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="text-center mb-6">
          <Share2 className="h-10 w-10 text-blue-600 mx-auto mb-2" />
          <h2 className="text-xl font-bold">Share Trip Details</h2>
          <p className="text-sm text-gray-500">
            Let friends & family know about your journey
          </p>
        </div>

        {/* Preview Card */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">{origin} → {destination}</span>
            <span className="text-blue-600 font-mono">{ticketCode}</span>
          </div>
          <div className="text-gray-500">
            📅 {tripDate} at {departureTime}
          </div>
          <div className="text-gray-500">
            💺 Seats: {seats}
          </div>
        </div>

        {/* Share Options */}
        <div className="space-y-3">
          <Button
            onClick={handleWhatsAppShare}
            className="w-full bg-green-500 hover:bg-green-600 flex items-center justify-center gap-2"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="h-5 w-5" />
            Share via WhatsApp
          </Button>
          
          <Button
            onClick={handleSMSShare}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            data-testid="share-sms"
          >
            <Smartphone className="h-5 w-5" />
            Share via SMS
          </Button>
          
          <Button
            onClick={handleCopyToClipboard}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            data-testid="share-copy"
          >
            {copied ? (
              <>
                <Check className="h-5 w-5 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-5 w-5" />
                Copy to Clipboard
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Simple share button component
export const ShareTripButton = ({ booking, variant = 'icon' }) => {
  const [showModal, setShowModal] = useState(false);

  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowModal(true);
          }}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          title="Share trip"
          data-testid="share-trip-btn"
        >
          <Share2 className="h-5 w-5 text-gray-500" />
        </button>
        {showModal && (
          <ShareTripModal booking={booking} onClose={() => setShowModal(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
        data-testid="share-trip-btn"
      >
        <Share2 className="h-4 w-4" />
        Share
      </Button>
      {showModal && (
        <ShareTripModal booking={booking} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

export default ShareTripModal;
